"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Droplets, Flame, PhoneCall, Pipette, ShieldAlert, Waves, ZapOff } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const warningSigns = [{
  id: "burst-pipes",
  title: "Burst or Rapidly Leaking Pipes",
  urgency: "Critical Risk",
  description: "Water gushing from ceiling, walls, or under floorboards causing rapid structural flooding.",
  action: "Shut off the main water valve immediately.",
  icon: Droplets,
  badgeVariant: "destructive" as const
}, {
  id: "sewage-backup",
  title: "Sewer Line Backup & Odors",
  urgency: "Biohazard Risk",
  description: "Dark water or black waste pushing back into tubs, floor drains, or multiple toilets simultaneously.",
  action: "Do not run any drains or appliances.",
  icon: Waves,
  badgeVariant: "destructive" as const
}, {
  id: "water-heater-leak",
  title: "Water Heater Tank Rupture",
  urgency: "High Urgency",
  description: "Standing water around the tank base, loud banging noises, or rust-tinted hot water streaming out.",
  action: "Switch off the unit circuit breaker and supply valve.",
  icon: Flame,
  badgeVariant: "destructive" as const
}, {
  id: "total-pressure-loss",
  title: "Sudden Complete Water Outage",
  urgency: "Urgent",
  description: "Zero water flow to all faucets without municipal interruption, signaling a major supply break.",
  action: "Inspect your water meter for continuous running.",
  icon: ZapOff,
  badgeVariant: "secondary" as const
}, {
  id: "overflowing-toilet",
  title: "Unstoppable Toilet Overflow",
  urgency: "High Urgency",
  description: "Water constantly spilling over the bowl rim despite attempts to plunge or stop the flapper.",
  action: "Turn the silver valve behind the toilet clockwise.",
  icon: ShieldAlert,
  badgeVariant: "destructive" as const
}, {
  id: "gas-smell-plumbing",
  title: "Sulphur or Rotten Egg Smells",
  urgency: "Immediate Hazard",
  description: "Foul sewer gas escaping dried p-traps or cracked vent stacks inside living spaces.",
  action: "Ventilate the space and evacuate if you suspect natural gas.",
  icon: AlertTriangle,
  badgeVariant: "destructive" as const
}];
export default function EmergencySigns() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencywarningsigns" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-semibold tracking-wide uppercase border-primary/30 text-primary">
            24/7 Triage Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Warning Signs You Need an Emergency Plumber
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Not all plumbing issues can wait until morning. If you spot any of the critical issues below in your Jonesboro property, take immediate action to avoid severe property loss.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {warningSigns.map((item, i) => {
          const IconComponent = item.icon;
          return <div key={item.id} data-index={i} className="h-full">
                <motion.div initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 15
            }} whileInView={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.35,
              delay: shouldReduceMotion ? 0 : i * 0.08
            }} className="h-full">
                  <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow duration-200 flex flex-col justify-between">
                    <div>
                      <CardHeader className="space-y-3 pb-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                            <IconComponent className="size-5" />
                          </div>
                          <Badge variant={item.badgeVariant} className="text-xs font-semibold">
                            {item.urgency}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl font-bold font-default text-foreground pt-1">
                          {item.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </CardDescription>
                      </CardContent>
                    </div>

                    <div className="px-6 pb-6 pt-2">
                      <div className="rounded-md bg-muted/60 p-3 border border-border/70 text-xs">
                        <span className="font-semibold text-foreground block mb-0.5">First Action:</span>
                        <span className="text-muted-foreground">{item.action}</span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </div>;
        })}
        </div>

        <div className="max-w-4xl mx-auto">
          <Alert className="border-primary/20 bg-primary/5 text-card-foreground p-6 rounded-xl shadow-xs">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 w-full">
              <div className="space-y-1">
                <AlertTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                  <ShieldAlert className="size-5 text-destructive inline" />
                  Dealing with active water or sewer damage right now?
                </AlertTitle>
                <AlertDescription className="text-muted-foreground text-sm">
                  Our emergency response team in Jonesboro is dispatched around the clock with live arrival estimates.
                </AlertDescription>
              </div>
              <div className="flex flex-wrap gap-3 shrink-0 w-full sm:w-auto">
                <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
                  <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Call (470) 847-9076</span>
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-semibold">
                  <Link to="/contact/">Request Dispatch</Link>
                </Button>
              </div>
            </div>
          </Alert>
        </div>
      </div>
    </section>;
}